import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  LogOut, Users, Building2, ShoppingBag, MessageSquare, KeyRound,
  Package, Boxes, Ticket, Truck, Mail, RotateCcw, ClipboardList,
} from "lucide-react";
import { api, adminApi } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

const PAYMENT_TABS = [
  { id: "paid", label: "Paid Orders", icon: ShoppingBag, status: "paid" },
  { id: "pending", label: "Pending", icon: ClipboardList, status: "pending" },
  { id: "refunded", label: "Refunded", icon: RotateCcw, status: "refunded" },
];

const FULFILLMENT_OPTIONS = ["new", "processing", "ready_for_pickup", "shipped", "delivered", "cancelled"];

export default function AdminPage() {
  const [token, setToken] = useState(adminApi.getToken());
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("paid");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    document.title = "Admin · Pickle Wall™";
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/admin/login", { password });
      adminApi.setToken(res.data.token);
      setToken(res.data.token);
      toast.success("Logged in");
    } catch {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    adminApi.clearToken();
    setToken(null);
  };

  const loadStats = useCallback(async () => {
    try {
      const s = await api.get("/admin/stats", { headers: adminApi.authHeader() });
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        toast.error("Session expired");
      }
    }
  }, []);

  useEffect(() => {
    if (token) loadStats();
  }, [token, loadStats]);

  if (!token) {
    return (
      <main id="main-content" className="bg-[#0a0a0a] text-white min-h-screen flex items-center justify-center px-6">
        <form onSubmit={login} className="w-full max-w-md glass rounded-3xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#FF6B00]/10 flex items-center justify-center">
              <KeyRound size={18} className="text-[#FF6B00]" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-white/50">Pickle Wall</div>
              <h1 className="font-display text-2xl font-bold uppercase tracking-tight">Admin Login</h1>
            </div>
          </div>
          <input
            data-testid={ADMIN.password}
            type="password"
            placeholder="Admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-[#0a0a0a] border border-white/10 focus:border-[#FF6B00] outline-none rounded-xl px-4 py-3 text-white placeholder-white/30 text-sm"
          />
          <button
            type="submit"
            data-testid={ADMIN.login}
            disabled={loading}
            className="mt-4 w-full bg-[#FF6B00] hover:bg-[#E66000] disabled:opacity-60 text-black font-bold uppercase tracking-wider text-sm px-7 py-4"
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
      </main>
    );
  }

  const NAV = [
    { id: "paid", label: "Orders", icon: ShoppingBag },
    { id: "abandoned", label: "Abandoned", icon: Package },
    { id: "inventory", label: "Inventory", icon: Boxes },
    { id: "customers", label: "Customers", icon: Users },
    { id: "discounts", label: "Discounts", icon: Ticket },
    { id: "shipping", label: "Shipping", icon: Truck },
    { id: "email", label: "Email", icon: Mail },
    { id: "leads", label: "Leads", icon: Users },
    { id: "inquiries", label: "Inquiries", icon: Building2 },
    { id: "contacts", label: "Contacts", icon: MessageSquare },
  ];

  return (
    <main id="main-content" className="bg-[#0a0a0a] text-white min-h-screen">
      <header className="border-b border-white/10 px-6 md:px-10 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#FF6B00] rounded-md flex items-center justify-center font-display font-black text-black">P</div>
          <div className="font-display font-bold uppercase tracking-tight">Pickle Wall Admin</div>
        </div>
        <button onClick={logout} data-testid={ADMIN.logout} className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white">
          <LogOut size={16} /> Logout
        </button>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
            {[
              { label: "Paid Orders", value: stats.orders },
              { label: "Leads", value: stats.leads },
              { label: "Pre-Orders", value: stats.preorders },
              { label: "Inquiries", value: stats.inquiries },
              { label: "Contacts", value: stats.contacts },
            ].map((s) => (
              <div key={s.label} className="bg-[#111] border border-white/5 rounded-2xl p-5">
                <div className="text-xs font-bold uppercase tracking-widest text-white/50">{s.label}</div>
                <div className="mt-2 font-display text-4xl font-black">{s.value ?? 0}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
          {NAV.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={ADMIN.tab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider transition-colors whitespace-nowrap ${
                tab === t.id ? "bg-[#FF6B00] text-black" : "bg-[#111] text-white/70 border border-white/10 hover:text-white"
              }`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {["paid", "pending", "refunded"].includes(tab) && <OrdersPanel onChange={loadStats} />}
        {tab === "abandoned" && <SimpleTable endpoint="/admin/abandoned" empty="No abandoned checkouts." transform={(r) => ({
          email: r.customer_email, name: r.customer_name || "—",
          cart_value: money(r.cart_value), status: r.recovery_status,
          emails_sent: r.recovery_emails_sent, created: new Date(r.created_at).toLocaleString(),
        })} />}
        {tab === "inventory" && <InventoryPanel />}
        {tab === "customers" && <SimpleTable endpoint="/admin/customers" empty="No customers yet." transform={(r) => ({
          name: r.name, email: r.email, phone: r.phone || "—",
          orders: r.orders_count || 0, lifetime: money(r.lifetime_value || 0),
        })} />}
        {tab === "discounts" && <DiscountsPanel />}
        {tab === "shipping" && <ShippingPanel />}
        {tab === "email" && <EmailPanel />}
        {tab === "leads" && <SimpleTable endpoint="/leads" empty="No leads yet." />}
        {tab === "inquiries" && <SimpleTable endpoint="/inquiries" empty="No inquiries yet." />}
        {tab === "contacts" && <SimpleTable endpoint="/contacts" empty="No contacts yet." />}
      </div>
    </main>
  );
}

/* ---------- Orders ---------- */
function OrdersPanel({ onChange }) {
  const [sub, setSub] = useState("paid");
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/admin/orders?payment_status=${sub}`, { headers: adminApi.authHeader() });
      setRows(res.data);
    } catch {
      toast.error("Failed to load orders");
    }
  }, [sub]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {PAYMENT_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setSub(t.status)}
            data-testid={`admin-orders-${t.id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${
              sub === t.status ? "bg-white text-black" : "bg-[#111] text-white/60 border border-white/10"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="bg-[#111] border border-white/5 rounded-2xl overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-10 text-center text-white/40">No {sub} orders.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-xs uppercase tracking-widest text-white/50">
                <tr>
                  {["Order", "Customer", "Total", "Payment", "Fulfillment", "Date", ""].map((h) => (
                    <th key={h} className="text-left px-4 py-3 font-bold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <>
                    <tr key={o.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`order-row-${o.order_number}`}>
                      <td className="px-4 py-3 font-mono text-[#FF6B00]">{o.order_number}</td>
                      <td className="px-4 py-3">{o.customer_name}<br /><span className="text-white/40 text-xs">{o.customer_email}</span></td>
                      <td className="px-4 py-3 font-bold">{money(o.total)}</td>
                      <td className="px-4 py-3"><Badge>{o.payment_status}</Badge></td>
                      <td className="px-4 py-3"><Badge orange>{o.fulfillment_status}</Badge></td>
                      <td className="px-4 py-3 text-white/60 text-xs">{new Date(o.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => setExpanded(expanded === o.id ? null : o.id)} className="text-[#FF6B00] text-xs font-bold uppercase">
                          {expanded === o.id ? "Close" : "Manage"}
                        </button>
                      </td>
                    </tr>
                    {expanded === o.id && (
                      <tr className="bg-black/40">
                        <td colSpan={7} className="px-4 py-5">
                          <OrderDetail order={o} onSaved={() => { load(); onChange?.(); }} />
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function OrderDetail({ order, onSaved }) {
  const [fs, setFs] = useState(order.fulfillment_status);
  const [carrier, setCarrier] = useState(order.shipping_carrier || "");
  const [tn, setTn] = useState(order.tracking_number || "");
  const [turl, setTurl] = useState(order.tracking_url || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/orders/${order.id}/fulfillment`,
        { fulfillment_status: fs, shipping_carrier: carrier, tracking_number: tn, tracking_url: turl },
        { headers: adminApi.authHeader() });
      toast.success(fs === "shipped" ? "Updated — shipment email sent" : "Order updated");
      onSaved?.();
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const resend = async () => {
    try {
      await api.post(`/admin/orders/${order.id}/resend-confirmation`, {}, { headers: adminApi.authHeader() });
      toast.success("Confirmation email resent");
    } catch {
      toast.error("Resend failed");
    }
  };

  const addr = order.shipping_address || {};
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Items</div>
        {(order.items || []).map((it, i) => (
          <div key={i} className="flex justify-between text-sm py-1 border-b border-white/5">
            <span>{it.name}{it.variant_note ? ` · ${it.variant_note}` : ""} × {it.quantity}</span>
            <span className="text-white/70">{money(it.unit_amount * it.quantity)}</span>
          </div>
        ))}
        <div className="mt-3 text-sm space-y-1 text-white/70">
          <div className="flex justify-between"><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
          {order.discount_amount ? <div className="flex justify-between text-[#FF6B00]"><span>Discount {order.discount_code}</span><span>-{money(order.discount_amount)}</span></div> : null}
          <div className="flex justify-between"><span>Shipping ({order.shipping_method})</span><span>{order.shipping_amount ? money(order.shipping_amount) : "FREE"}</span></div>
          <div className="flex justify-between"><span>Tax</span><span>{money(order.tax_amount)}</span></div>
          <div className="flex justify-between font-bold text-white border-t border-white/10 pt-1"><span>Total</span><span>{money(order.total)}</span></div>
        </div>
        <div className="mt-3 text-xs text-white/50">
          Phone: {order.customer_phone || "—"}<br />
          Ship to: {addr.line1 ? `${addr.line1}, ${addr.city}, ${addr.state} ${addr.postal_code}, ${addr.country}` : "—"}<br />
          Stripe PI: <span className="font-mono">{order.stripe_payment_intent_id || "—"}</span><br />
          Confirmation email: {order.email_confirmation_sent ? "sent" : "not sent"}
          <button onClick={resend} className="ml-2 text-[#FF6B00] underline">Resend</button>
        </div>
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Fulfillment</div>
        <select value={fs} onChange={(e) => setFs(e.target.value)} data-testid="order-fulfillment-select"
          className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2 text-sm mb-3">
          {FULFILLMENT_OPTIONS.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
        </select>
        <input placeholder="Carrier (e.g. UPS)" value={carrier} onChange={(e) => setCarrier(e.target.value)} className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2 text-sm mb-2" />
        <input placeholder="Tracking number" value={tn} onChange={(e) => setTn(e.target.value)} data-testid="order-tracking-number" className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2 text-sm mb-2" />
        <input placeholder="Tracking URL" value={turl} onChange={(e) => setTurl(e.target.value)} className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2 text-sm mb-3" />
        <button onClick={save} disabled={saving} data-testid="order-save-fulfillment" className="bg-[#FF6B00] hover:bg-[#E66000] text-black font-bold uppercase tracking-wider text-xs px-5 py-3 rounded-lg">
          {saving ? "Saving..." : "Save & Notify"}
        </button>
      </div>
    </div>
  );
}

/* ---------- Inventory ---------- */
function InventoryPanel() {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/inventory", { headers: adminApi.authHeader() });
      setRows(res.data);
    } catch { toast.error("Failed to load inventory"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const adjust = async (sku, variant, delta) => {
    try {
      await api.post("/admin/inventory/adjust", { sku, variant, delta, reason: "Manual admin adjustment" }, { headers: adminApi.authHeader() });
      load();
    } catch { toast.error("Adjust failed"); }
  };

  return (
    <div className="bg-[#111] border border-white/5 rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-xs uppercase tracking-widest text-white/50">
            <tr>{["Product", "SKU", "Variant", "Available", "Status", "Adjust"].map((h) => <th key={h} className="text-left px-4 py-3 font-bold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-white/5" data-testid={`inv-row-${r.sku}-${r.variant || 'na'}`}>
                <td className="px-4 py-3">{r.name}</td>
                <td className="px-4 py-3 font-mono text-white/60">{r.sku}</td>
                <td className="px-4 py-3">{r.variant || "—"}</td>
                <td className="px-4 py-3 font-bold">{r.available}</td>
                <td className="px-4 py-3">{r.low_stock ? <Badge>LOW STOCK</Badge> : <Badge orange>OK</Badge>}</td>
                <td className="px-4 py-3">
                  <button onClick={() => adjust(r.sku, r.variant, -1)} className="w-7 h-7 rounded bg-white/10 mr-1">−</button>
                  <button onClick={() => adjust(r.sku, r.variant, 1)} className="w-7 h-7 rounded bg-[#FF6B00] text-black font-bold">+</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Discounts ---------- */
function DiscountsPanel() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ code: "", discount_type: "percent", value: 10, max_redemptions: "", expires_days: "", first_order_only: false });
  const load = useCallback(async () => {
    try { setRows((await api.get("/admin/discounts", { headers: adminApi.authHeader() })).data); }
    catch { toast.error("Failed to load discounts"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { code: form.code, discount_type: form.discount_type, value: Number(form.value), first_order_only: form.first_order_only };
      if (form.max_redemptions) payload.max_redemptions = Number(form.max_redemptions);
      if (form.expires_days) payload.expires_days = Number(form.expires_days);
      await api.post("/admin/discounts", payload, { headers: adminApi.authHeader() });
      toast.success(`Code ${form.code.toUpperCase()} created`);
      setForm({ ...form, code: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Create failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <form onSubmit={create} className="glass rounded-2xl p-6 space-y-3" data-testid="discount-form">
        <div className="font-display text-lg font-bold uppercase">New Discount Code</div>
        <input placeholder="CODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required data-testid="discount-code" className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2 text-sm uppercase" />
        <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2 text-sm">
          <option value="percent">Percentage %</option>
          <option value="amount">Fixed $ off</option>
        </select>
        <input type="number" placeholder={form.discount_type === "percent" ? "% off (1-100)" : "$ off"} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2 text-sm" />
        <input type="number" placeholder="Max redemptions (optional)" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2 text-sm" />
        <input type="number" placeholder="Expires in days (optional)" value={form.expires_days} onChange={(e) => setForm({ ...form, expires_days: e.target.value })} className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2 text-sm" />
        <label className="flex items-center gap-2 text-sm text-white/70">
          <input type="checkbox" checked={form.first_order_only} onChange={(e) => setForm({ ...form, first_order_only: e.target.checked })} /> First order only
        </label>
        <button type="submit" data-testid="discount-create" className="w-full bg-[#FF6B00] text-black font-bold uppercase tracking-wider text-xs px-5 py-3 rounded-lg">Create Code</button>
      </form>
      <div className="lg:col-span-2 bg-[#111] border border-white/5 rounded-2xl overflow-hidden">
        {rows.length === 0 ? <div className="p-10 text-center text-white/40">No discount codes yet.</div> : (
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-xs uppercase tracking-widest text-white/50">
              <tr>{["Code", "Type", "Value", "Redeemed", "Max", "Active"].map((h) => <th key={h} className="text-left px-4 py-3 font-bold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} className="border-t border-white/5">
                  <td className="px-4 py-3 font-mono text-[#FF6B00]">{d.code}</td>
                  <td className="px-4 py-3">{d.type}</td>
                  <td className="px-4 py-3">{d.type === "percent" ? `${d.value}%` : money(d.value * 100)}</td>
                  <td className="px-4 py-3">{d.times_redeemed}</td>
                  <td className="px-4 py-3">{d.max_redemptions || "∞"}</td>
                  <td className="px-4 py-3">{d.active ? <Badge orange>Active</Badge> : <Badge>Off</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ---------- Shipping ---------- */
function ShippingPanel() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/settings/shipping", { headers: adminApi.authHeader() }).then((r) => setS(r.data)).catch(() => toast.error("Failed to load"));
  }, []);
  if (!s) return <div className="text-white/40 p-6">Loading…</div>;

  const dollars = (c) => (c / 100).toFixed(2);

  const update = (field, dollarVal) => setS({ ...s, [field]: Math.round(parseFloat(dollarVal || 0) * 100) });
  const commit = async () => {
    try {
      await api.put("/admin/settings/shipping", {
        standard_rate: s.standard_rate,
        hoodie_only_rate: s.hoodie_only_rate,
        free_shipping_threshold: s.free_shipping_threshold,
        local_pickup_enabled: s.local_pickup_enabled,
        local_pickup_rate: s.local_pickup_rate,
        fulfillment_message: s.fulfillment_message,
      }, { headers: adminApi.authHeader() });
      toast.success("Shipping settings saved");
    } catch { toast.error("Save failed"); }
  };

  return (
    <div className="glass rounded-2xl p-6 max-w-lg space-y-4" data-testid="shipping-settings">
      <div className="font-display text-xl font-bold uppercase">Shipping Settings</div>
      <Field label="Standard shipping ($)" value={dollars(s.standard_rate)} onChange={(v) => update("standard_rate", v)} testId="ship-standard" />
      <Field label="Hoodie-only shipping ($)" value={dollars(s.hoodie_only_rate)} onChange={(v) => update("hoodie_only_rate", v)} testId="ship-hoodie" />
      <Field label="Free shipping threshold ($)" value={dollars(s.free_shipping_threshold)} onChange={(v) => update("free_shipping_threshold", v)} testId="ship-threshold" />
      <label className="flex items-center gap-2 text-sm text-white/70">
        <input type="checkbox" checked={s.local_pickup_enabled} onChange={(e) => setS({ ...s, local_pickup_enabled: e.target.checked })} data-testid="ship-pickup" /> Enable free local pickup
      </label>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-white/50 mb-1">Fulfillment message</div>
        <input value={s.fulfillment_message} onChange={(e) => setS({ ...s, fulfillment_message: e.target.value })} className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2 text-sm" />
      </div>
      <button onClick={commit} data-testid="shipping-save" className="bg-[#FF6B00] text-black font-bold uppercase tracking-wider text-xs px-5 py-3 rounded-lg">Save Settings</button>
    </div>
  );
}

/* ---------- Email ---------- */
function EmailPanel() {
  const [cfg, setCfg] = useState(null);
  const [log, setLog] = useState([]);
  useEffect(() => {
    api.get("/admin/settings/email", { headers: adminApi.authHeader() }).then((r) => setCfg(r.data)).catch(() => {});
    api.get("/admin/email-log", { headers: adminApi.authHeader() }).then((r) => setLog(r.data)).catch(() => {});
  }, []);
  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-6 max-w-lg" data-testid="email-settings">
        <div className="font-display text-xl font-bold uppercase mb-4">Email Settings</div>
        {cfg ? (
          <div className="text-sm space-y-2 text-white/70">
            <div className="flex justify-between"><span>Sender Name</span><span className="text-white">{cfg.from_name}</span></div>
            <div className="flex justify-between"><span>Admin Order Email</span><span className="text-white">{cfg.admin_order_email}</span></div>
            <div className="flex justify-between"><span>Support Email</span><span className="text-white">{cfg.support_email}</span></div>
            <div className="flex justify-between"><span>Email Delivery</span><span>{cfg.email_configured ? <Badge orange>Active</Badge> : <Badge>Off</Badge>}</span></div>
          </div>
        ) : "Loading…"}
      </div>
      <div className="bg-[#111] border border-white/5 rounded-2xl overflow-hidden">
        <div className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-white/50 border-b border-white/5">Email Delivery Log</div>
        {log.length === 0 ? <div className="p-8 text-center text-white/40 text-sm">No emails sent yet.</div> : (
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <tbody>
                {log.map((e, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="px-4 py-2 text-white/80">{e.kind}</td>
                    <td className="px-4 py-2 text-white/60">{e.to}</td>
                    <td className="px-4 py-2">{e.status === "sent" ? <Badge orange>sent</Badge> : <Badge>{e.status}</Badge>}</td>
                    <td className="px-4 py-2 text-white/40 text-xs">{new Date(e.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Shared ---------- */
function SimpleTable({ endpoint, empty, transform }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(endpoint, { headers: adminApi.authHeader() }).then((r) => setRows(r.data)).catch(() => toast.error("Failed to load"));
  }, [endpoint]);
  const display = rows.map((r) => (transform ? transform(r) : r));
  if (display.length === 0) return <div className="bg-[#111] border border-white/5 rounded-2xl p-10 text-center text-white/40">{empty}</div>;
  const cols = Object.keys(display[0]).filter((k) => k !== "id" && k !== "_id");
  return (
    <div className="bg-[#111] border border-white/5 rounded-2xl overflow-hidden overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-white/[0.03] text-xs uppercase tracking-widest text-white/50">
          <tr>{cols.map((c) => <th key={c} className="text-left px-4 py-3 font-bold">{c.replace(/_/g, " ")}</th>)}</tr>
        </thead>
        <tbody>
          {display.map((row, i) => (
            <tr key={i} className="border-t border-white/5 hover:bg-white/[0.02]">
              {cols.map((c) => <td key={c} className="px-4 py-3 text-white/85">{c.includes("created") && row[c] && !isNaN(Date.parse(row[c])) ? new Date(row[c]).toLocaleString() : String(row[c] ?? "")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Badge({ children, orange }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
      orange ? "bg-[#FF6B00]/15 text-[#FF6B00]" : "bg-white/10 text-white/70"
    }`}>{children}</span>
  );
}

function Field({ label, value, onChange, testId }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-white/50 mb-1">{label}</div>
      <input type="number" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2 text-sm" />
    </div>
  );
}
